import { useState, useEffect } from "react";
import { Card, Button, Modal, message, Select, Divider } from "antd";
import {
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import useSubscriptionService from "../../../../services/subscriptionService";
import { useAuth } from "../../../../context/authContext";
import { useLanguage } from "../../../../context/LanguageContext";
import dashboardContent from "../../../../utils/dashboardContent";

const { confirm } = Modal;
const { Option } = Select;

const AboManagement = () => {
  const { user, subscriptionLevel, fetchUser } = useAuth();
  const {
    createPortalSession,
    cancelSubscription,
    upgradeSubscription,
    changeBillingCycle,
  } = useSubscriptionService();

  const { language } = useLanguage();
  const text = dashboardContent[language].subscription;

  // Pricing data - könnte auch aus dashboardContent kommen
  const pricingData = {
    BASIC: {
      monthlyPrice: 19.9,
      annualPrice: 14.9,
      features: [
        language === "de" ? "1 Feedback-Formular" : "1 Feedback Form",
        language === "de" ? "1 QR-Code" : "1 QR Code",
        language === "de" ? "Basis-Reports" : "Basic Reports",
        language === "de" ? "Ohne Branding" : "No Branding",
        language === "de" ? "Keine KI-Funktionen" : "No AI Features",
      ],
    },
    STANDARD: {
      monthlyPrice: 29.9,
      annualPrice: 24.9,
      features: [
        language === "de" ? "5 Feedback-Formulare" : "5 Feedback Forms",
        language === "de" ? "5 QR-Codes" : "5 QR Codes",
        language === "de" ? "Eigenes Branding" : "Custom Branding",
        language === "de" ? "Detaillierte Analysen" : "Detailed Analytics",
        language === "de"
          ? "Monatliche KI-Empfehlungen"
          : "Monthly AI Recommendations",
      ],
    },
    PREMIUM: {
      monthlyPrice: 39.9,
      annualPrice: 34.9,
      features: [
        language === "de" ? "10 Feedback-Formulare" : "10 Feedback Forms",
        language === "de" ? "10 QR-Codes" : "10 QR Codes",
        language === "de" ? "Erweiterte KI-Analysen" : "Advanced AI Analytics",
        language === "de" ? "Eigenes Branding" : "Custom Branding",
        language === "de"
          ? "Monatliche KI-Empfehlungen"
          : "Monthly AI Recommendations",
      ],
    },
  };

  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState(null);

  // Neue States für Custom Confirm Modals
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isUpgradeConfirmModalOpen, setIsUpgradeConfirmModalOpen] =
    useState(false);
  const [isBillingCycleModalOpen, setIsBillingCycleModalOpen] = useState(false);

  // States für Checkboxen
  const [cancelConfirmChecked, setCancelConfirmChecked] = useState(false);
  const [upgradeConfirmChecked, setUpgradeConfirmChecked] = useState(false);
  const [billingCycleConfirmChecked, setBillingCycleConfirmChecked] =
    useState(false);

  const currentBillingCycle =
    user.subscription.contractDuration > 1 ? "annual" : "monthly";
  const currentPlan = (user?.subscription?.planName || "BASIC").toUpperCase();
  console.log(currentPlan);

  const [aboStatus, setAboStatus] = useState(
    user?.subscription?.status || "Unbekannt"
  );
  const [aboStufe, setAboStufe] = useState(
    user?.subscription?.plan || "Unbekannt"
  );
  const [laufzeitEnde, setLaufzeitEnde] = useState(
    user?.subscription?.endDate
      ? new Date(user.subscription.endDate)
      : new Date()
  );

  // Prüfen ob Laufzeitwechsel möglich ist (nur von monthly zu annual)
  const canChangeBillingCycle =
    currentBillingCycle === "monthly" && !user.subscription.pendingCancellation;

  // Verfügbare Upgrade-Optionen basierend auf aktuellem Plan
  const getAvailableUpgrades = () => {
    const plans = ["BASIC", "STANDARD", "PREMIUM"];
    const currentIndex = plans.indexOf(currentPlan);
    return plans.slice(currentIndex + 1); // Nur höhere Pläne
  };

  // Aktuelle Preise basierend auf Billing Cycle
  const getCurrentPrice = (plan) => {
    return currentBillingCycle === "annual"
      ? pricingData[plan]?.annualPrice
      : pricingData[plan]?.monthlyPrice;
  };

  // Savings Berechnung
  const getSavings = (plan) => {
    const monthly = pricingData[plan]?.monthlyPrice || 0;
    const annual = pricingData[plan]?.annualPrice || 0;
    return Math.max(0, monthly - annual); // Sicherstellen dass es nicht negativ wird
  };

  const openUpgradeModal = () => {
    setSelectedUpgradePlan(null);
    setIsUpgradeModalOpen(true);
  };

  useEffect(() => {
    if (user?.subscription) {
      setAboStatus(user.subscription.status);
      setAboStufe(user.subscription.plan);
      if (user.subscription.endDate) {
        setLaufzeitEnde(new Date(user.subscription.endDate));
      }
    }
  }, [user]);

  const formatDate = (date) => date.toLocaleDateString("de-DE");

  const showConfirm = (action, callback, customContent = null) => {
    confirm({
      title: `${text.confirmTitle} ${action} ${text.confirmAction}?`,
      icon: <ExclamationCircleOutlined />,
      content: customContent || text.confirmContent,
      okText: text.yes,
      cancelText: text.no,
      okButtonProps: {
        style: {
          backgroundColor: "#f97316", // orange-500
          borderColor: "#f97316",
          color: "white",
        },
      },
      onOk() {
        callback();
      },
      onCancel() {
        console.log("Aktion abgebrochen");
      },
    });
  };

  const handleKuendigen = () => {
    setCancelConfirmChecked(false);
    setIsCancelModalOpen(true);
  };

  const confirmCancellation = async () => {
    const result = await cancelSubscription(user.stripeCustomerId);
    if (result) {
      message.success(result.message || text.cancelSuccess);
      await fetchUser();
      setIsCancelModalOpen(false);
    }
  };

  // Upgrade-Handler: Ruft den Backend-Endpunkt zum Upgrade auf.
  const handleUpgraden = () => {
    if (!selectedUpgradePlan) return;
    setUpgradeConfirmChecked(false);
    setIsUpgradeConfirmModalOpen(true);
  };

  const confirmUpgrade = async () => {
    const result = await upgradeSubscription({
      customerId: user.stripeCustomerId,
      newPlan: selectedUpgradePlan,
      billingCycle: currentBillingCycle,
    });

    if (result) {
      message.success(result.message || text.upgradeSuccess);
      await fetchUser();
      setIsUpgradeModalOpen(false);
      setIsUpgradeConfirmModalOpen(false);
    }
  };

  // Laufzeitwechsel Handler
  const handleBillingCycleChange = () => {
    setBillingCycleConfirmChecked(false);
    setIsBillingCycleModalOpen(true);
  };

  const confirmBillingCycleChange = async () => {
    const result = await changeBillingCycle("annual");
    if (result) {
      await fetchUser(); // User-Daten neu laden
      setIsBillingCycleModalOpen(false);
    }
  };

  // Öffnet das Stripe Customer Portal
  const handleAboVerwalten = async () => {
    const url = await createPortalSession(user.stripeCustomerId);
    if (url) {
      window.location.href = url;
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-100">
      <Card className="w-full max-w-md shadow-lg">
        <h2 className="text-2xl font-bold mb-4 text-center text-orange-500">
          {text.title}
        </h2>
        <div className="mb-4 space-y-2 text-sm text-gray-700">
          <p>
            <strong>{text.plan}</strong> {user.subscription.planName}
          </p>
          <p>
            <strong>{text.status}</strong>{" "}
            {user.subscription.status === "active" ||
            user.subscription.status === "incomplete"
              ? language === "de"
                ? "Aktiv"
                : "Active"
              : user.subscription.status}
          </p>
          <p>
            <strong>{text.startDate}</strong>{" "}
            {new Date(user.subscription.startDate).toLocaleDateString(
              language === "de" ? "de-DE" : "en-US"
            )}
          </p>
          <p>
            <strong>{text.currentPeriodEnd}</strong>{" "}
            {(() => {
              const contractDuration = user.subscription.contractDuration;
              const startDate = new Date(user.subscription.startDate);
              const nextBilling = new Date(user.subscription.nextBillingDate);

              const calculatedEnd =
                contractDuration > 1
                  ? new Date(
                      new Date(startDate).setMonth(
                        startDate.getMonth() + contractDuration
                      )
                    )
                  : nextBilling;

              return calculatedEnd.toLocaleDateString(
                language === "de" ? "de-DE" : "en-US"
              );
            })()}
          </p>
          <p>
            <strong>{text.nextBillingDate}</strong>{" "}
            {new Date(user.subscription.nextBillingDate).toLocaleDateString(
              language === "de" ? "de-DE" : "en-US"
            )}
          </p>
          <p>
            <strong>{text.contractDuration}</strong>{" "}
            {user.subscription.contractDuration}{" "}
            {language === "de" ? "Monate" : "months"}
          </p>
          <p>
            <strong>{text.pendingCancellation}</strong>{" "}
            {user.subscription.pendingCancellation
              ? language === "de"
                ? "Kündigung vorgemerkt"
                : "Pending cancellation"
              : language === "de"
              ? "Nicht gekündigt"
              : "Not cancelled"}
          </p>
          {user.subscription.contractDuration > 1 && (
            <div className="mt-2 text-xs text-gray-500 italic">
              {language === "de"
                ? "Hinweis: Bei einem 12-Monats-Abo wird die Kündigung erst zum Ende der Laufzeit wirksam."
                : "Note: With a 12-month subscription, cancellation takes effect only at the end of the term."}
            </div>
          )}
        </div>
        <div className="space-y-4">
          <Button
            onClick={handleKuendigen}
            danger
            className="w-full hover:bg-red-600 hover:border-red-600 hover:text-white transition-colors duration-300"
          >
            {text.cancelButton}
          </Button>

          <Button
            onClick={openUpgradeModal}
            disabled={getAvailableUpgrades().length === 0}
            className={`w-full text-white transition-colors duration-300 ${
              getAvailableUpgrades().length === 0
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-green-500 border-green-500 hover:bg-green-600 hover:border-green-600"
            }`}
          >
            {getAvailableUpgrades().length === 0
              ? language === "de"
                ? "Höchster Plan erreicht"
                : "Highest plan reached"
              : text.upgradeButton}
          </Button>

          {/* Laufzeitwechsel Button - nur bei monatlich und ohne Kündigung */}
          {canChangeBillingCycle && (
            <Button
              onClick={handleBillingCycleChange}
              className="w-full bg-purple-500 border-purple-500 text-white hover:bg-purple-600 hover:border-purple-600 transition-colors duration-300"
            >
              {language === "de"
                ? "💰 Auf 12-Monats-Laufzeit wechseln & sparen"
                : "💰 Switch to 12-month term & save"}
            </Button>
          )}

          <Button
            onClick={handleAboVerwalten}
            className="w-full bg-blue-500 border-blue-500 text-white hover:bg-blue-600 hover:border-blue-600 transition-colors duration-300"
          >
            {text.manageButton}
          </Button>
        </div>

        {/* Info-Text für Laufzeitwechsel */}
        {canChangeBillingCycle && (
          <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-xs text-purple-700">
              {language === "de"
                ? "💡 Wechseln Sie auf 12 Monate Laufzeit und erhalten Sie einen günstigeren monatlichen Preis bei gleicher monatlicher Abrechnung!"
                : "💡 Switch to a 12-month term and get a lower monthly price with the same monthly billing!"}
            </p>
          </div>
        )}
      </Card>

      {/* Enhanced Upgrade Modal with Pricing */}
      <Modal
        title={text.upgradeModalTitle}
        open={isUpgradeModalOpen}
        okButtonProps={{
          disabled: !selectedUpgradePlan,
          style: {
            backgroundColor: "#f97316", // orange-500
            borderColor: "#f97316",
            color: "white",
          },
        }}
        onOk={handleUpgraden}
        onCancel={() => setIsUpgradeModalOpen(false)}
        width={700}
        focusTriggerAfterClose={false}
        autoFocus={false}
        getContainer={() => document.body}
      >
        <div className="space-y-4">
          <p>{text.upgradeModalDescription}</p>

          {/* Current Plan Info */}
          <div className="p-3 bg-gray-50 rounded-lg border">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium text-gray-800">
                  {language === "de" ? "Aktueller Plan:" : "Current Plan:"}{" "}
                  {currentPlan}
                </p>
                <p className="text-sm text-gray-600">
                  €{getCurrentPrice(currentPlan)}/
                  {language === "de" ? "Monat" : "month"}
                </p>
              </div>
              <InfoCircleOutlined className="text-gray-400" />
            </div>
          </div>

          <Divider className="my-4" />

          {/* Plan Selection */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-800">
              {language === "de"
                ? "Verfügbare Upgrades:"
                : "Available Upgrades:"}
            </h4>

            {/* Plan Selection - Nur höhere Pläne anzeigen */}
            {getAvailableUpgrades().length > 0 ? (
              getAvailableUpgrades().map((plan) => (
                <div
                  key={plan}
                  className={`border rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                    selectedUpgradePlan === plan
                      ? "border-orange-500 bg-orange-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                  onClick={() => setSelectedUpgradePlan(plan)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={selectedUpgradePlan === plan}
                          onChange={() => setSelectedUpgradePlan(plan)}
                          className="text-orange-500"
                        />
                        <h5 className="font-semibold text-lg">{plan}</h5>
                      </div>

                      <div className="mt-2">
                        <p className="text-lg font-bold text-green-600">
                          €{getCurrentPrice(plan)}/
                          {language === "de" ? "Monat" : "month"}
                        </p>
                        {currentBillingCycle === "monthly" && (
                          <p className="text-xs text-gray-500">
                            {language === "de"
                              ? `Bei 12-Monats-Laufzeit: €${
                                  pricingData[plan].annualPrice
                                }/Monat (€${getSavings(plan).toFixed(
                                  1
                                )} Ersparnis)`
                              : `12-month term: €${
                                  pricingData[plan].annualPrice
                                }/month (€${getSavings(plan).toFixed(
                                  1
                                )} savings)`}
                          </p>
                        )}
                      </div>

                      <ul className="mt-3 space-y-1">
                        {pricingData[plan]?.features.map((feature, index) => (
                          <li
                            key={index}
                            className="text-sm text-gray-600 flex items-center gap-2"
                          >
                            <span className="text-green-500 font-bold">✓</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center p-8 text-gray-500">
                <p className="text-lg font-medium">
                  {language === "de"
                    ? "Sie haben bereits den höchsten verfügbaren Plan!"
                    : "You already have the highest available plan!"}
                </p>
                <p className="text-sm mt-2">
                  {language === "de"
                    ? "Genießen Sie alle Premium-Features."
                    : "Enjoy all premium features."}
                </p>
              </div>
            )}
          </div>

          <p className="text-xs text-gray-500 italic mt-4 p-3 bg-blue-50 rounded">
            <InfoCircleOutlined className="mr-2" />
            {language === "de"
              ? `Dein aktueller Zahlungszyklus (${
                  currentBillingCycle === "monthly" ? "monatlich" : "jährlich"
                }) bleibt beim Upgrade erhalten. Die Preisdifferenz wird anteilig berechnet.`
              : `Your current billing cycle (${
                  currentBillingCycle === "monthly" ? "monthly" : "annually"
                }) will be retained during the upgrade. The price difference will be calculated proportionally.`}
          </p>
        </div>
      </Modal>

      {/* Custom Cancel Confirmation Modal */}
      <Modal
        title={
          <span className="text-red-600">
            {language === "de" ? "Abo kündigen?" : "Cancel subscription?"}
          </span>
        }
        open={isCancelModalOpen}
        onCancel={() => setIsCancelModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsCancelModalOpen(false)}>
            {language === "de" ? "Abbrechen" : "Cancel"}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            disabled={!cancelConfirmChecked}
            onClick={confirmCancellation}
          >
            {language === "de" ? "Ja, kündigen" : "Yes, cancel"}
          </Button>,
        ]}
        width={600}
      >
        <div>
          <p className="mb-3">
            {language === "de"
              ? "⚠️ Wichtige Informationen zur Kündigung:"
              : "⚠️ Important cancellation information:"}
          </p>
          <ul className="list-disc pl-5 space-y-2 text-sm">
            <li>
              {language === "de"
                ? `Ihr Abo läuft noch bis zum ${(() => {
                    const contractDuration = user.subscription.contractDuration;
                    const startDate = new Date(user.subscription.startDate);
                    const nextBilling = new Date(
                      user.subscription.nextBillingDate
                    );
                    const calculatedEnd =
                      contractDuration > 1
                        ? new Date(
                            new Date(startDate).setMonth(
                              startDate.getMonth() + contractDuration
                            )
                          )
                        : nextBilling;
                    return calculatedEnd.toLocaleDateString("de-DE");
                  })()} weiter`
                : `Your subscription continues until ${(() => {
                    const contractDuration = user.subscription.contractDuration;
                    const startDate = new Date(user.subscription.startDate);
                    const nextBilling = new Date(
                      user.subscription.nextBillingDate
                    );
                    const calculatedEnd =
                      contractDuration > 1
                        ? new Date(
                            new Date(startDate).setMonth(
                              startDate.getMonth() + contractDuration
                            )
                          )
                        : nextBilling;
                    return calculatedEnd.toLocaleDateString("en-US");
                  })()}`}
            </li>
            <li>
              {language === "de"
                ? "Sie behalten vollen Zugriff bis zum Ende der Laufzeit"
                : "You keep full access until the end of the term"}
            </li>
            <li>
              {language === "de"
                ? "Nach der Kündigung können Sie jederzeit wieder ein neues Abo abschließen"
                : "After cancellation, you can subscribe again at any time"}
            </li>
            {user.subscription.contractDuration > 1 && (
              <li className="text-amber-600 font-medium">
                {language === "de"
                  ? "Bei 12-Monats-Laufzeit: Kündigung wird zum Laufzeitende wirksam"
                  : "12-month term: Cancellation takes effect at end of term"}
              </li>
            )}
          </ul>

          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={cancelConfirmChecked}
                onChange={(e) => setCancelConfirmChecked(e.target.checked)}
                className="mt-1 mr-3 h-4 w-4 text-red-600"
              />
              <span className="text-sm text-red-800 font-medium">
                {language === "de"
                  ? "✓ Ich verstehe die Bedingungen und möchte mein Abo definitiv kündigen"
                  : "✓ I understand the conditions and want to cancel my subscription"}
              </span>
            </label>
          </div>
        </div>
      </Modal>

      {/* Enhanced Upgrade Confirmation Modal */}
      <Modal
        title={
          <span className="text-green-600">
            {language === "de"
              ? "Plan-Upgrade bestätigen?"
              : "Confirm plan upgrade?"}
          </span>
        }
        open={isUpgradeConfirmModalOpen}
        onCancel={() => setIsUpgradeConfirmModalOpen(false)}
        footer={[
          <Button
            key="cancel"
            onClick={() => setIsUpgradeConfirmModalOpen(false)}
          >
            {language === "de" ? "Abbrechen" : "Cancel"}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            disabled={!upgradeConfirmChecked}
            onClick={confirmUpgrade}
            style={{
              backgroundColor: "#f97316",
              borderColor: "#f97316",
            }}
          >
            {language === "de" ? "Ja, upgraden" : "Yes, upgrade"}
          </Button>,
        ]}
        width={700}
      >
        <div>
          <p className="mb-4">
            {language === "de"
              ? "📈 Plan-Upgrade Details:"
              : "📈 Plan upgrade details:"}
          </p>

          {/* Upgrade Comparison */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Current Plan */}
            <div className="p-4 bg-gray-50 rounded-lg border">
              <h5 className="font-semibold text-gray-800 mb-2">
                {language === "de" ? "Aktuell" : "Current"}
              </h5>
              <p className="font-medium">{currentPlan}</p>
              <p className="text-lg font-bold text-gray-600">
                €{getCurrentPrice(currentPlan)}/
                {language === "de" ? "Monat" : "month"}
              </p>
            </div>

            {/* New Plan */}
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h5 className="font-semibold text-green-800 mb-2">
                {language === "de" ? "Neu" : "New"}
              </h5>
              <p className="font-medium text-green-700">
                {selectedUpgradePlan}
              </p>
              <p className="text-lg font-bold text-green-600">
                €{getCurrentPrice(selectedUpgradePlan)}/
                {language === "de" ? "Monat" : "month"}
              </p>
              <p className="text-sm text-green-600 font-medium">
                +€
                {(
                  getCurrentPrice(selectedUpgradePlan) -
                  getCurrentPrice(currentPlan)
                ).toFixed(1)}
                /{language === "de" ? "Monat" : "month"}
              </p>
            </div>
          </div>

          {/* Features Comparison */}
          {selectedUpgradePlan && (
            <div className="mb-4">
              <h6 className="font-semibold mb-3">
                {language === "de" ? "Neue Features:" : "New Features:"}
              </h6>
              <ul className="space-y-2">
                {pricingData[selectedUpgradePlan]?.features.map(
                  (feature, index) => (
                    <li
                      key={index}
                      className="text-sm text-green-600 flex items-center gap-2"
                    >
                      <span className="text-green-500 font-bold">✓</span>
                      {feature}
                    </li>
                  )
                )}
              </ul>
            </div>
          )}

          <ul className="list-disc pl-5 space-y-2 text-sm text-gray-600 mb-4">
            <li>
              {language === "de"
                ? `Zahlungszyklus: ${
                    currentBillingCycle === "monthly" ? "Monatlich" : "Jährlich"
                  } (bleibt unverändert)`
                : `Billing cycle: ${
                    currentBillingCycle === "monthly" ? "Monthly" : "Annual"
                  } (remains unchanged)`}
            </li>
            <li>
              {language === "de"
                ? "Das Upgrade wird sofort wirksam"
                : "The upgrade takes effect immediately"}
            </li>
            <li>
              {language === "de"
                ? "Sie zahlen anteilig die Preisdifferenz für den aktuellen Zeitraum"
                : "You pay the prorated price difference for the current period"}
            </li>
            <li>
              {language === "de"
                ? "Alle neuen Features sind sofort verfügbar"
                : "All new features are immediately available"}
            </li>
          </ul>

          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={upgradeConfirmChecked}
                onChange={(e) => setUpgradeConfirmChecked(e.target.checked)}
                className="mt-1 mr-3 h-4 w-4 text-green-600"
              />
              <span className="text-sm text-green-800 font-medium">
                {language === "de"
                  ? "✓ Ich möchte das Upgrade durchführen und verstehe die Kosten"
                  : "✓ I want to proceed with the upgrade and understand the costs"}
              </span>
            </label>
          </div>
        </div>
      </Modal>

      {/* Enhanced Billing Cycle Change Modal */}
      <Modal
        title={
          <span className="text-purple-600">
            {language === "de"
              ? "Laufzeit auf 12 Monate ändern?"
              : "Change term to 12 months?"}
          </span>
        }
        open={isBillingCycleModalOpen}
        onCancel={() => setIsBillingCycleModalOpen(false)}
        footer={[
          <Button
            key="cancel"
            onClick={() => setIsBillingCycleModalOpen(false)}
          >
            {language === "de" ? "Abbrechen" : "Cancel"}
          </Button>,
          <Button
            key="confirm"
            type="primary"
            disabled={!billingCycleConfirmChecked}
            onClick={confirmBillingCycleChange}
            style={{
              backgroundColor: "#f97316",
              borderColor: "#f97316",
            }}
          >
            {language === "de" ? "Ja, wechseln" : "Yes, switch"}
          </Button>,
        ]}
        width={700}
      >
        <div>
          <p className="mb-4">
            {language === "de"
              ? "💰 Laufzeitwechsel Details:"
              : "💰 Billing cycle change details:"}
          </p>

          {/* Savings Comparison */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="p-4 bg-gray-50 rounded-lg border">
              <h5 className="font-semibold text-gray-800 mb-2">
                {language === "de"
                  ? "Aktuell (Monatlich)"
                  : "Current (Monthly)"}
              </h5>
              <p className="font-medium">{currentPlan}</p>
              <p className="text-lg font-bold text-gray-600">
                €{pricingData[currentPlan]?.monthlyPrice}/
                {language === "de" ? "Monat" : "month"}
              </p>
              <p className="text-sm text-gray-500">
                {language === "de"
                  ? "Monatlich kündbar"
                  : "Monthly cancellable"}
              </p>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h5 className="font-semibold text-purple-800 mb-2">
                {language === "de" ? "Neu (12 Monate)" : "New (12 months)"}
              </h5>
              <p className="font-medium text-purple-700">{currentPlan}</p>
              <p className="text-lg font-bold text-purple-600">
                €{pricingData[currentPlan]?.annualPrice}/
                {language === "de" ? "Monat" : "month"}
              </p>
              <p className="text-sm text-green-600 font-medium">
                {language === "de" ? "Sparen Sie" : "Save"} €
                {getSavings(currentPlan).toFixed(1)}/
                {language === "de" ? "Monat" : "month"}
              </p>
            </div>
          </div>

          {/* Annual Savings Highlight */}
          <div className="p-4 bg-green-50 rounded-lg border border-green-200 mb-4">
            <div className="text-center">
              <p className="text-sm text-green-700">
                {language === "de" ? "Jährliche Ersparnis:" : "Annual savings:"}
              </p>
              <p className="text-2xl font-bold text-green-600">
                €{(getSavings(currentPlan) * 12).toFixed(0)}
              </p>
              <p className="text-xs text-green-600">
                {language === "de"
                  ? "bei gleicher monatlicher Abrechnung"
                  : "with the same monthly billing"}
              </p>
            </div>
          </div>

          <ul className="list-disc pl-5 space-y-2 text-sm">
            <li>
              {language === "de"
                ? "Aktuelle Laufzeit: 1 Monat (monatlich kündbar)"
                : "Current term: 1 month (monthly cancellable)"}
            </li>
            <li>
              {language === "de"
                ? "Neue Laufzeit: 12 Monate (Mindestlaufzeit)"
                : "New term: 12 months (minimum term)"}
            </li>
            <li className="text-green-600 font-medium">
              {language === "de"
                ? "Sie erhalten sofort einen günstigeren monatlichen Preis"
                : "You immediately get a lower monthly price"}
            </li>
            <li>
              {language === "de"
                ? "Abrechnung: Weiterhin jeden Monat (nicht jährlich!)"
                : "Billing: Still monthly (not annually!)"}
            </li>
            <li>
              {language === "de"
                ? "Kündigung: Erst nach 12 Monaten möglich"
                : "Cancellation: Only possible after 12 months"}
            </li>
            <li>
              {language === "de"
                ? "Upgrade/Downgrade: Jederzeit weiterhin möglich"
                : "Upgrade/Downgrade: Still possible at any time"}
            </li>
          </ul>

          <div className="mt-4 p-3 bg-amber-50 rounded border border-amber-200">
            <p className="text-amber-800 text-sm font-medium">
              ⚠️ {language === "de" ? "Wichtig:" : "Important:"}
            </p>
            <p className="text-amber-700 text-xs mt-1">
              {language === "de"
                ? "Nach dem Wechsel können Sie 12 Monate lang nicht kündigen, erhalten aber dafür den günstigeren Preis."
                : "After switching, you cannot cancel for 12 months, but you get the lower price in return."}
            </p>
          </div>

          <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded">
            <label className="flex items-start cursor-pointer">
              <input
                type="checkbox"
                checked={billingCycleConfirmChecked}
                onChange={(e) =>
                  setBillingCycleConfirmChecked(e.target.checked)
                }
                className="mt-1 mr-3 h-4 w-4 text-purple-600"
              />
              <span className="text-sm text-purple-800 font-medium">
                {language === "de"
                  ? "✓ Ich verstehe die 12-Monats-Bindung und möchte wechseln"
                  : "✓ I understand the 12-month commitment and want to switch"}
              </span>
            </label>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AboManagement;
